import {
  Box,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import AuthForm from "components/Auth";
import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { getParams, getSource, trackEvent } from "utils/tracking";
import { useLogin } from "utils/useLogin";
import { useSubscription } from "utils/useSubscription";

import AnnualBanner from "./Banner";
import AnnualFeatures from "./Features";
import AnnualPlans from "./Plans";

const PENDING_PLAN_KEY = "annual_pending_plan";

const AnnualSplashPage = () => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const source = getSource();
  const { setAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchUserInfo, fetchUserLoading } = useLogin();
  const { currentOrg } = useContext(OrganizationUserContext);
  const isSignedIn = getParams("signed_in") === "true";
  const { handleCheckout } = useSubscription();
  const fromRoutes = `/${routes.annual}`;

  // Rehydrate plan from sessionStorage so it survives the Clerk post-auth redirect
  const [plan, setPlan] = useState<Plans | null>(
    () => (sessionStorage.getItem(PENDING_PLAN_KEY) as Plans | null) ?? null,
  );

  // Dialog is open when we're on the sign-in or sign-up sub-route
  const isAuthRoute =
    location.pathname.includes("/sign-in") ||
    location.pathname.includes("/sign-up");

  const redirectUrl = `/${routes.annual}?signed_in=true`;

  const closeDialog = () => {
    navigate(`/${routes.annual}${location.search}`, { replace: true });
  };

  const checkOutForSplashPage = (selectedPlan: Plans) => {
    setPlan(selectedPlan);
    if (!currentOrg) {
      sessionStorage.setItem(PENDING_PLAN_KEY, selectedPlan);
      navigate(`/${routes.annual}/sign-up${location.search}`);
      return;
    }
    sessionStorage.removeItem(PENDING_PLAN_KEY);
    if (currentOrg?.isVaUser && selectedPlan === Plans.VA_YEARLY) {
      setAlert(
        "You already is a VA user. Please go to Settings page to manage your subscription.",
        "info",
      );
      navigate(`/${routes.home}${location.search}`, { replace: true });
    } else {
      handleCheckout(
        selectedPlan,
        source || "",
        window.location.pathname,
        SplashPage.annual,
      );
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (isSignedIn && plan) {
      checkOutForSplashPage(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id]);

  return (
    <Box>
      {/* Background content — always rendered */}
      <AnnualBanner />
      <AnnualFeatures />
      <AnnualPlans checkOutForSplashPage={checkOutForSplashPage} />

      {/* Loading overlay */}
      <Fade in={fetchUserLoading} unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(16, 24, 40, 0.55)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              backgroundColor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "20px",
              px: 5,
              py: 4,
            }}
          >
            <CircularProgress
              size={44}
              thickness={3}
              sx={{ color: "#E8F36D" }}
            />
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: "1rem",
                letterSpacing: "0.01em",
              }}
            >
              Setting things up…
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}
            >
              Just a moment while we prepare your checkout.
            </Typography>
          </Box>
        </Box>
      </Fade>

      {/* Auth dialog — slides over the background when on sign-in/sign-up routes */}
      <Dialog
        open={isAuthRoute && !fetchUserLoading}
        onClose={closeDialog}
        fullScreen={isMobileScreen}
        PaperProps={{
          sx: {
            borderRadius: isMobileScreen ? 0 : "16px",
            overflow: "hidden",
            width: "fit-content",
            px: 2,
            minWidth: 250,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",

            pt: 2,
          }}
        >
          <IconButton onClick={closeDialog} size="small">
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Box>

        <Routes>
          <Route
            path="/sign-in/*"
            element={
              <AuthForm
                redirectUrl={redirectUrl}
                action={AuthAction.SIGN_IN}
                signInUrl={`${fromRoutes}/sign-in${location.search}`}
                signUpUrl={`${fromRoutes}/sign-up${location.search}`}
                fromRoutes={fromRoutes}
                isDialog
              />
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <AuthForm
                redirectUrl={redirectUrl}
                action={AuthAction.SIGN_UP}
                signInUrl={`${fromRoutes}/sign-in${location.search}`}
                signUpUrl={`${fromRoutes}/sign-up${location.search}`}
                fromRoutes={fromRoutes}
                isDialog
              />
            }
          />
        </Routes>
      </Dialog>
    </Box>
  );
};

export default AnnualSplashPage;
